import React, { useState } from "react";
import { getAuthInstance } from "./firebase";
import { confirmPasswordReset } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ErrorText from "./components/form/ErrorText";
import { Eye, EyeOff } from "lucide-react";
import { resetPasswordSchema } from "./utils/validationSchema";
import { AppLoader } from "./components/AppLoader";

// Add validation schema

const ResetPassword = ({ oobCode }) => {
  const navigate = useNavigate();
  const auth = getAuthInstance();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onChange",
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, data.password);
      toast.success("Password reset successfully.");
      navigate("/");
    } catch (err) {
      toast.error("Something went wrong while resetting password.");
    }
    setIsLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className="relative bg-white p-8 rounded-3xl shadow-xl w-[480px] mx-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-medium mb-2 font-['Inter']">
            Reset Password
          </h2>
          <p className="text-gray-500">Enter your new password below.</p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              {...register("password")}
              placeholder="Password"
              className={`w-full p-4 border ${
                errors.password ? "border-red-500" : "border-gray-200"
              } rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFFD63] focus:border-transparent transition-all`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-[30px] -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
            {errors.password && (
              <ErrorText>{errors.password.message}</ErrorText>
            )}
          </div>

          <div className="relative">
            <input
              type="password"
              {...register("confirmPassword")}
              placeholder="Confirm Password"
              className={`w-full p-4 border ${
                errors.confirmPassword ? "border-red-500" : "border-gray-200"
              } rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFFD63] focus:border-transparent transition-all`}
            />
            {errors.confirmPassword && (
              <ErrorText>{errors.confirmPassword.message}</ErrorText>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-black text-white py-4 px-4 rounded-xl hover:bg-black/90 transition-all font-medium"
          >
            {isLoading ? (
              <div className="flex justify-center items-center">
                <AppLoader color="white" />
              </div>
            ) : (
              "Reset"
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/")}
            className="text-gray-600 hover:text-black transition-colors"
          >
            Don't have to reset password? Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
